<div class="ts1-review" [class.ts1-review--preview]="readOnly">
	@if (!readOnly) {
		<div class="ts1-review__sticky-chrome" id="ts1-review-sticky-chrome">
			<div class="ts1-review__header-bar flex h-[40px] items-center bg-blue-20 px-4 text-lg font-medium text-black">
				Review &amp; Final Rating
			</div>

			<div class="ts1-review__candidate-switcher">
				<button
					type="button"
					class="ts1-review__nav-btn"
					(click)="prevCandidate.emit()"
					[disabled]="currentIndex === 0"
				>
					<mat-icon>chevron_left</mat-icon>
				</button>

				<div class="ts1-review__candidate-center">
					<span class="ts1-review__candidate-name">{{ currentName }}</span>
				</div>

				<button
					type="button"
					class="ts1-review__nav-btn"
					(click)="nextCandidate.emit()"
					[disabled]="currentIndex >= candidateNames.length - 1"
				>
					<mat-icon>chevron_right</mat-icon>
				</button>
			</div>
		</div>
	}

	<section class="ts1-review__section ts1-review__section--competency" id="ts1-review-section-competency">
		<div class="ts1-review__section-header">Rating per Competency</div>
		<div class="ts1-review__rating-legend" aria-label="Rating A, B, and C definitions">
			@for (item of ratingLegendItems; track item.key) {
				<div class="ts1-review__rating-legend-card">
					<div class="ts1-review__rating-legend-badge" aria-hidden="true">{{ item.key }}</div>
					<span class="ts1-review__rating-legend-title">Rating {{ item.key }} · {{ item.label }}</span>
				</div>
			}
		</div>
		<div class="ts1-review-competencies">
			@for (block of competencyBlocks; track block.code) {
				<div
					class="ts1-review-comp"
					[id]="'ts1-review-competency-' + block.code"
					[class.ts1-review-comp--not-graded]="block.notGraded"
					[class.ts1-review-comp--highlight]="isScrollHighlightActive('competency', block.code)"
					[attr.aria-disabled]="block.notGraded ? true : null"
				>
					<div class="ts1-review-comp__score-panel" [ngClass]="finalScorePanelClasses(block)">
						<span class="ts1-review-comp__code">{{ block.code }}</span>
						<span class="ts1-review-comp__final-label">FINAL SCORE</span>
						<span
							class="ts1-review-comp__final-value"
							[class.ts1-review-comp__final-value--no-score]="isCompetencyFinalScorePending(block)"
							>{{ finalScoreLabel(block) }}</span
						>
					</div>

					<div class="ts1-review-comp__ratings">
						<!-- Rating A — pre-calculated (How Many), read-only -->
						<div class="ts1-review-comp__row">
							<div class="ts1-review-comp__label">{{ block.code }} – A</div>
							<div class="ts1-review-grid__options">
								@for (opt of competencyRatingOptions; track opt) {
									<div
										class="ts1-review-grid__cell ts1-review-grid__cell--readonly"
										[ngClass]="competencyReadonlyCellClasses(block.ratingA, opt)"
										[class.ts1-review-grid__cell--empty]="
											!isCompetencySelected(competencyRatingAsNumber(block.ratingA), opt)
										"
									>
										{{ competencyOptionLabel(opt) }}
									</div>
								}
							</div>
						</div>

						<!-- Rating B — pre-calculated (How Often), read-only -->
						<div class="ts1-review-comp__row">
							<div class="ts1-review-comp__label">{{ block.code }} – B</div>
							<div class="ts1-review-grid__options">
								@for (opt of competencyRatingOptions; track opt) {
									<div
										class="ts1-review-grid__cell ts1-review-grid__cell--readonly"
										[ngClass]="competencyReadonlyCellClasses(block.ratingB, opt)"
										[class.ts1-review-grid__cell--empty]="
											!isCompetencySelected(competencyRatingAsNumber(block.ratingB), opt)
										"
									>
										{{ competencyOptionLabel(opt) }}
									</div>
								}
							</div>
						</div>

						<!-- Rating C — instructor editable (read-only when competency not graded or preview mode) -->
						<div class="ts1-review-comp__row" [class.ts1-review-comp__row--editable]="!block.notGraded && !readOnly">
							<div class="ts1-review-comp__label">{{ block.code }} – C</div>
							<div class="ts1-review-grid__options">
								@for (opt of competencyRatingOptions; track opt) {
									@if (block.notGraded || readOnly) {
										<div
											class="ts1-review-grid__cell ts1-review-grid__cell--readonly"
											[ngClass]="competencyReadonlyCellClasses(block.ratingC, opt)"
											[class.ts1-review-grid__cell--empty]="
												!isCompetencySelected(competencyRatingAsNumber(block.ratingC), opt)
											"
										>
											{{ competencyOptionLabel(opt) }}
										</div>
									} @else {
										<button
											type="button"
											class="ts1-review-grid__cell"
											[ngClass]="competencyCellClasses(block, opt)"
											[class.ts1-review-grid__cell--empty]="
												!isCompetencySelected(competencyRatingAsNumber(block.ratingC), opt)
											"
											[class.ts1-review-grid__cell--readonly]="!isCurrentCandidateEditable()"
											[attr.aria-readonly]="!isCurrentCandidateEditable() ? true : null"
											[attr.tabindex]="!isCurrentCandidateEditable() ? -1 : null"
											(click)="selectCompetencyRating(block, opt)"
										>
											{{ competencyOptionLabel(opt) }}
										</button>
									}
								}
							</div>
						</div>
					</div>
				</div>
			}
		</div>
	</section>

	<section
		class="ts1-review__section ts1-review__section--maneuver-ratings"
		id="ts1-review-section-maneuver"
		[class.ts1-review__section--collapsed]="!maneuverRatingsExpanded"
		[class.ts1-review__section--highlight]="isScrollHighlightActive('maneuver')"
	>
		<button
			type="button"
			class="ts1-review__section-header ts1-review__section-header--collapsible"
			(click)="toggleManeuverRatings()"
			[attr.aria-expanded]="maneuverRatingsExpanded"
		>
			<span>Final Maneuver Review</span>
			<mat-icon class="ts1-review__section-chevron">
				{{ maneuverRatingsExpanded ? "expand_less" : "expand_more" }}
			</mat-icon>
		</button>
		@if (maneuverRatingsExpanded) {
			<div class="ts1-review-grid ts1-review-grid--maneuver">
				<div class="ts1-review-grid__role-header">
					<span class="ts1-review-grid__label-col"></span>
					<span>PF</span>
					<div class="ts1-review-grid__divider-v"></div>
					<span>PM</span>
				</div>

				@for (row of maneuverRows; track row.maneuverCode) {
					<div class="ts1-review-grid__row" [id]="'ts1-review-maneuver-' + row.maneuverCode">
						<div class="ts1-review-grid__label">{{ row.label }}</div>
						<div
							class="ts1-review-grid__options"
							[class.ts1-review-grid__options--highlight]="
								isScrollHighlightActive('maneuver', row.maneuverCode + ':pf')
							"
						>
							@for (opt of ratingOptions; track opt) {
								@if (!isManeuverColumnEditable("pf")) {
									<div
										class="ts1-review-grid__cell ts1-review-grid__cell--readonly"
										[ngClass]="maneuverCellClasses(row, 'pf', opt)"
										[class.ts1-review-grid__cell--empty]="!isSelected(maneuverCellValue(row, 'pf'), opt)"
									>
										{{ optionLabel(opt) }}
									</div>
								} @else {
									<button
										type="button"
										class="ts1-review-grid__cell"
										[ngClass]="maneuverCellClasses(row, 'pf', opt)"
										[class.ts1-review-grid__cell--empty]="!isSelected(maneuverCellValue(row, 'pf'), opt)"
										(click)="selectManeuverRating(row, 'pf', opt)"
									>
										{{ optionLabel(opt) }}
									</button>
								}
							}
						</div>
						<div class="ts1-review-grid__divider-v"></div>
						<div
							class="ts1-review-grid__options"
							[class.ts1-review-grid__options--highlight]="
								isScrollHighlightActive('maneuver', row.maneuverCode + ':pm')
							"
						>
							@for (opt of ratingOptions; track opt) {
								@if (!isManeuverColumnEditable("pm")) {
									<div
										class="ts1-review-grid__cell ts1-review-grid__cell--readonly"
										[ngClass]="maneuverCellClasses(row, 'pm', opt)"
										[class.ts1-review-grid__cell--empty]="!isSelected(maneuverCellValue(row, 'pm'), opt)"
									>
										{{ optionLabel(opt) }}
									</div>
								} @else {
									<button
										type="button"
										class="ts1-review-grid__cell"
										[ngClass]="maneuverCellClasses(row, 'pm', opt)"
										[class.ts1-review-grid__cell--empty]="!isSelected(maneuverCellValue(row, 'pm'), opt)"
										(click)="selectManeuverRating(row, 'pm', opt)"
									>
										{{ optionLabel(opt) }}
									</button>
								}
							}
						</div>
					</div>
				}
			</div>
		}
	</section>

	@if (!readOnly) {
		<section class="ts1-review__section ts1-review__section--notes">
			<div class="ts1-review__notes-label">Instructor Comment</div>
			<div
				class="ts1-review__notes-box ts1-review__notes-box--comments border-1 flex h-[150px] flex-col items-stretch justify-stretch border border-[#CFE3FF] text-[85%] uppercase"
			>
				<div class="flex-1 overflow-y-auto overflow-x-hidden p-3">
					@if (displaySummaryComments.length === 0) {
						<span class="text-grey-40">No summary comments.</span>
					} @else {
						<div class="ts1-summary-list">
							@for (comment of displaySummaryComments; track $index) {
								<div class="ts1-summary-row">
									<span class="ts1-summary-row__segment ts1-summary-row__segment--candidate">
										@if (comment?.ob !== "REPEAT") {
											[
											@if (getCommentCandidateLabel(comment); as candidateLabel) {
												<span [class]="getCommentCandidateLabelClass(comment)">{{ candidateLabel }}</span>
											}
											@if (
												getCommentCandidateLabel(comment) &&
												(comment.candidateRole === CANDIDATE_ROLE.PF || comment.candidateRole === CANDIDATE_ROLE.PM)
											) {
												<span class="text-grey-60"> · </span>
											}
											@if (comment.candidateRole === CANDIDATE_ROLE.PF) {
												<span class="text-purple-90">PF</span>
											}
											@if (comment.candidateRole === CANDIDATE_ROLE.PM) {
												<span class="text-purple-90">PM</span>
											}
											]
										}
									</span>
									<span class="ts1-summary-row__segment ts1-summary-row__segment--maneuver">
										{{ summaryManeuverLabel(comment) }}
									</span>
									<span class="ts1-summary-row__segment ts1-summary-row__segment--status">
										@if (comment?.isObservationComment) {
											<span>{{ comment?.ob }}</span>
										} @else if (comment?.isManeuverOutcome) {
											@if (comment?.remarks) {
												<span [class]="getOutcomeRateClass(comment.rate)">{{ comment.remarks }}</span>
											}
										} @else if (comment?.isTemCompetencyOutcome) {
											<span>{{ summaryCompetencyStatusLabel(comment) }}</span>
											@if (comment?.remarks) {
												<span [class]="getOutcomeRateClass(comment.rate)">{{ " { " + comment.remarks + " }" }}</span>
											}
										} @else if (comment?.ob !== "TEM" && comment?.ob !== "REPEAT") {
											@if (comment?.isReset || comment?.lastActionDisabled || comment?.isDisabled) {
												<span class="text-yellow">{{ comment?.ob }}</span>
											} @else {
												<span [class.text-green]="comment?.action" [class.text-red]="!comment?.action">
													{{ comment?.ob }}
												</span>
											}
											@if (comment?.isReset) {
												<span class="text-yellow">{{ " { Undo }" }}</span>
											} @else if (comment?.lastActionDisabled) {
												<span class="text-yellow">{{ " { Undo N/A }" }}</span>
											} @else if (comment?.isDisabled) {
												<span class="text-yellow">{{ " { N/A }" }}</span>
											} @else if (comment?.action != null) {
												<span [class.text-green]="comment?.action" [class.text-red]="!comment?.action">
													{{ comment?.action ? " { added }" : " { removed }" }}
												</span>
											}
										}
										@if (comment?.ob === "REPEAT") {
											<span class="text-yellow">{{ comment?.ob }}</span>
										}
									</span>
									<span class="ts1-summary-row__segment ts1-summary-row__segment--comment normal-case">
										@if (hasInstructorComment(comment)) {
											{{ comment.instructorComment }}
										} @else if (isMissingRequiredComment(comment)) {
											<span class="ts1-summary-missing-comment-badge">
												<mat-icon class="ts1-summary-missing-comment-badge__icon" aria-hidden="true">
													error_outline
												</mat-icon>
												<span>Comment required</span>
											</span>
										}
									</span>
								</div>
							}
						</div>
					}
				</div>
				<div class="flex items-center justify-end px-3 pb-3">
					<button
						type="button"
						(click)="viewSummaryModal()"
						class="size-6 rounded-sm bg-red pt-1 text-center text-white lg:size-8"
						matTooltip="View/Edit Comments"
					>
						<mat-icon class="text-xs lg:text-base">edit</mat-icon>
					</button>
				</div>
			</div>
		</section>
	}

	@if (!readOnly) {
		<section class="ts1-review__section ts1-review__section--notes">
			<div class="ts1-review__notes-label">General Observation</div>
			<div class="ts1-review__notes-box border-1 flex min-h-[120px] flex-col border border-[#CFE3FF]">
				<div class="flex-1 overflow-y-auto overflow-x-hidden p-3 text-sm normal-case">
					@if (generalObservationText) {
						<span>{{ generalObservationText }}</span>
					} @else {
						<span class="text-grey-40">No general observation added.</span>
					}
				</div>
				<div class="flex items-center justify-end px-3 pb-3">
					<button
						type="button"
						(click)="openGeneralObservationEditor()"
						class="size-6 rounded-sm bg-red pt-1 text-center text-white lg:size-8"
						matTooltip="Edit General Observation"
					>
						<mat-icon class="text-xs lg:text-base">edit</mat-icon>
					</button>
				</div>
			</div>
		</section>
	}

	@if (useStructuredReminderNotes && reminderNoteResponses) {
		<section
			class="ts1-review__section ts1-review__section--notes"
			id="ts1-review-section-reminderNotes"
			[class.ts1-review__section--highlight]="isScrollHighlightActive('reminderNotes')"
		>
			<div class="ts1-review__notes-label">Additional Training/Assessment Details</div>
			<app-ets1-rating-reminder-notes
				[reminderNoteResponses]="reminderNoteResponses"
				[readOnly]="readOnly"
				[highlightNoteId]="reminderNoteScrollHighlightId"
				(statusChanged)="onReminderNoteStatusChanged()"
				(textChanged)="onReminderNoteStatusChanged()"
			></app-ets1-rating-reminder-notes>
		</section>
	} @else if (reminderNotesEnabled) {
		<section class="ts1-review__section ts1-review__section--notes">
			<div class="ts1-review__notes-label">Additional Training/Assessment Details</div>
			<div class="ts1-review__notes-box border-1 flex min-h-[120px] flex-col border border-[#CFE3FF]">
				<div class="flex-1 overflow-y-auto overflow-x-hidden p-3 text-sm normal-case">
					@if (reminderNotesText) {
						<span class="uppercase">{{ reminderNotesText }}</span>
					} @else if (!readOnly) {
						<span class="text-grey-40">No additional training/assessment details added.</span>
					} @else {
						<span class="text-grey-40">—</span>
					}
				</div>
				@if (!readOnly) {
					<div class="flex items-center justify-end px-3 pb-3">
						<button
							type="button"
							(click)="openReminderNotesEditor()"
							class="size-6 rounded-sm bg-red pt-1 text-center text-white lg:size-8"
							matTooltip="Edit Additional Training/Assessment Details"
						>
							<mat-icon class="text-xs lg:text-base">edit</mat-icon>
						</button>
					</div>
				}
			</div>
		</section>
	}

	<section
		class="ts1-review__section ts1-review__section--outcome"
		id="ts1-review-section-outcome"
		[class.ts1-review__section--highlight]="isScrollHighlightActive('outcome')"
	>
		<div class="ts1-review-outcome">
			<div class="ts1-review-outcome__title">Final Training/Check Session Outcome</div>

			@if (!readOnly) {
				<div
					class="ts1-review-outcome__category"
					id="ts1-review-outcome-category"
					[class.ts1-review-outcome__category--highlight]="isScrollHighlightActive('outcome', 'category')"
				>
					<span
						class="ts1-review-outcome__category-label"
						[class.ts1-review-outcome__category-label--active]="isTrainingCategory"
					>
						Training
					</span>
					<button
						type="button"
						class="ts1-review-outcome__switch"
						[class.ts1-review-outcome__switch--check]="!isTrainingCategory"
						(click)="setTrainingCategory(!isTrainingCategory)"
						aria-label="Toggle between Training and Check session"
					>
						<span class="ts1-review-outcome__switch-thumb" aria-hidden="true"></span>
					</button>
					<span
						class="ts1-review-outcome__category-label"
						[class.ts1-review-outcome__category-label--active]="!isTrainingCategory"
					>
						Check
					</span>
				</div>
			}

			<div class="ts1-review-reco">
				<div
					class="ts1-review-reco__row"
					id="ts1-review-outcome-sessionCheck"
					[class.ts1-review-reco__row--highlight]="isScrollHighlightActive('outcome', 'sessionCheck')"
				>
					<span class="ts1-review-reco__label">{{ sessionCompletedLabel }}</span>
					<div class="ts1-review-reco__options">
						<label class="ts1-review-reco__option">
							<input
								type="radio"
								[name]="recommendationFieldName('sessionCheck')"
								[disabled]="readOnly"
								[checked]="isRecommendationSelected('sessionCheck', true)"
								(change)="setRecommendation('sessionCheck', true)"
							/>
							<span>Yes</span>
						</label>
						<label class="ts1-review-reco__option">
							<input
								type="radio"
								[name]="recommendationFieldName('sessionCheck')"
								[disabled]="readOnly"
								[checked]="isRecommendationSelected('sessionCheck', false)"
								(change)="setRecommendation('sessionCheck', false)"
							/>
							<span>No</span>
						</label>
					</div>
				</div>

				<div
					class="ts1-review-reco__row"
					id="ts1-review-outcome-objectiveCheck"
					[class.ts1-review-reco__row--highlight]="isScrollHighlightActive('outcome', 'objectiveCheck')"
				>
					<span class="ts1-review-reco__label">{{ objectivesLabel }}</span>
					<div class="ts1-review-reco__options">
						<label class="ts1-review-reco__option">
							<input
								type="radio"
								[name]="recommendationFieldName('objectiveCheck')"
								[disabled]="readOnly"
								[checked]="isRecommendationSelected('objectiveCheck', true)"
								(change)="setRecommendation('objectiveCheck', true)"
							/>
							<span>Yes</span>
						</label>
						<label class="ts1-review-reco__option">
							<input
								type="radio"
								[name]="recommendationFieldName('objectiveCheck')"
								[disabled]="readOnly"
								[checked]="isRecommendationSelected('objectiveCheck', false)"
								(change)="setRecommendation('objectiveCheck', false)"
							/>
							<span>No</span>
						</label>
					</div>
				</div>

				@if (clearedForCheckVisible) {
					<div
						class="ts1-review-reco__row"
						id="ts1-review-outcome-clearanceCheck"
						[class.ts1-review-reco__row--highlight]="isScrollHighlightActive('outcome', 'clearanceCheck')"
					>
						<span class="ts1-review-reco__label">Cleared for Check</span>
						<div class="ts1-review-reco__options">
							<label class="ts1-review-reco__option">
								<input
									type="radio"
									[name]="recommendationFieldName('clearanceCheck')"
									[disabled]="readOnly"
									[checked]="isClearanceSelected('N/A')"
									(change)="setRecommendation('clearanceCheck', 'N/A')"
								/>
								<span>N/A</span>
							</label>
							<label class="ts1-review-reco__option">
								<input
									type="radio"
									[name]="recommendationFieldName('clearanceCheck')"
									[disabled]="readOnly"
									[checked]="isClearanceSelected(true)"
									(change)="setRecommendation('clearanceCheck', true)"
								/>
								<span>Yes</span>
							</label>
							<label class="ts1-review-reco__option">
								<input
									type="radio"
									[name]="recommendationFieldName('clearanceCheck')"
									[disabled]="readOnly"
									[checked]="isClearanceSelected(false)"
									(change)="setRecommendation('clearanceCheck', false)"
								/>
								<span>No</span>
							</label>
						</div>
					</div>
				}
			</div>

			<div
				class="ts1-review-grading"
				id="ts1-review-outcome-finalRating"
				[class.ts1-review-grading--highlight]="isScrollHighlightActive('outcome', 'finalRating')"
			>
				<div class="ts1-review-grading__label-col">
					<span class="ts1-review-grading__accent" aria-hidden="true"></span>
					<span class="ts1-review-grading__label">OVERALL GRADING</span>
				</div>
				@for (grade of gradingOptions; track grade) {
					@if (readOnly) {
						<div
							class="ts1-review-grading__cell ts1-review-grading__cell--readonly"
							[class.ts1-review-grading__cell--empty]="!isGradingSelected(grade)"
							[ngClass]="gradingCellClasses(grade)"
						>
							{{ grade }}
						</div>
					} @else {
						<button
							type="button"
							class="ts1-review-grading__cell"
							[class.ts1-review-grading__cell--empty]="!isGradingSelected(grade)"
							[ngClass]="gradingCellClasses(grade)"
							(click)="selectOverallGrading(grade)"
						>
							{{ grade }}
						</button>
					}
				}
			</div>
		</div>
	</section>
</div>
